import type { MembershipRole } from "@safeplate/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  GraduationCap,
  MailPlus,
  KeyRound,
  Pencil,
  Plus,
  ShieldCheck,
  UserRoundCheck,
  Users,
  X,
} from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import {
  changeMembership,
  createStaff,
  grantMembership,
  inviteStaff,
  listStaff,
  setStaffQuickPin,
  updateStaff,
  type Invitation,
  type StaffProfile,
} from "../setup/setup-api.js";
import { TrainingWorkspacePage } from "../training/training-page.js";

function StaffEditor({
  item,
  locationId,
  onClose,
}: {
  item?: StaffProfile;
  locationId: string;
  onClose: () => void;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const input = {
        display_name: String(form.get("display_name") ?? "").trim(),
        email: String(form.get("email") ?? "").trim() || null,
        job_title: String(form.get("job_title") ?? "").trim() || null,
        location_ids: [locationId],
      };
      return item
        ? updateStaff(item.id, {
            ...input,
            status: form.get("status") === "inactive" ? "inactive" : "active",
          })
        : createStaff({
            ...input,
            quick_pin: String(form.get("quick_pin") ?? ""),
          });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-3 backdrop-blur-sm sm:p-6">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="mx-auto my-8 w-full max-w-lg rounded-3xl bg-surface p-5 shadow-2xl sm:p-7"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          save.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Staff profile
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              {item ? "Edit team member" : "Add team member"}
            </h2>
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor={`${id}-name`}>Display name</Label>
            <Input
              className="mt-1.5"
              defaultValue={item?.display_name ?? ""}
              id={`${id}-name`}
              name="display_name"
              required
            />
          </div>
          <div>
            <Label htmlFor={`${id}-email`}>Email</Label>
            <Input
              className="mt-1.5"
              defaultValue={item?.email ?? ""}
              id={`${id}-email`}
              name="email"
              type="email"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-job`}>Job title</Label>
            <Input
              className="mt-1.5"
              defaultValue={item?.job_title ?? ""}
              id={`${id}-job`}
              name="job_title"
              placeholder="Chef de partie"
            />
          </div>
          {item ? (
            <div>
              <Label htmlFor={`${id}-status`}>Status</Label>
              <select
                className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm"
                defaultValue={item.status}
                id={`${id}-status`}
                name="status"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          ) : null}
          {!item ? (
            <div>
              <Label htmlFor={`${id}-pin`}>Quick-access PIN</Label>
              <Input
                autoComplete="off"
                className="mt-1.5"
                id={`${id}-pin`}
                inputMode="numeric"
                maxLength={6}
                minLength={6}
                name="quick_pin"
                pattern="[0-9]{6}"
                placeholder="Six digits"
                required
                type="password"
              />
              <p className="mt-1.5 text-xs leading-5 text-stone-500">
                They can use this after their invitation is accepted. Avoid
                repeated or sequential numbers.
              </p>
            </div>
          ) : null}
        </div>
        {save.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800" role="alert">
            {displayError(save.error)}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={save.isPending} type="submit">
            {save.isPending ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function QuickPinEditor({
  item,
  locationId,
  onClose,
}: {
  item: StaffProfile;
  locationId: string;
  onClose: () => void;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (pin: string) => setStaffQuickPin(item.id, pin),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
      onClose();
    },
  });
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-3 backdrop-blur-sm sm:p-6">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="mx-auto my-8 w-full max-w-md rounded-3xl bg-surface p-5 shadow-2xl sm:p-7"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          mutation.mutate(String(new FormData(event.currentTarget).get("pin") ?? ""));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">Quick access</p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              {item.quick_pin_enabled ? "Change" : "Set"} {item.display_name}’s PIN
            </h2>
          </div>
          <Button aria-label="Close" onClick={onClose} size="icon" variant="ghost"><X className="size-4" /></Button>
        </div>
        <p className="mt-3 text-sm leading-6 text-stone-600">
          This replaces any previous PIN and signs this person out of other
          quick-access sessions for this workplace.
        </p>
        <div className="mt-5">
          <Label htmlFor={`${id}-value`}>New six-digit PIN</Label>
          <Input
            autoComplete="off"
            className="mt-2 text-center text-xl tracking-[0.3em]"
            id={`${id}-value`}
            inputMode="numeric"
            maxLength={6}
            minLength={6}
            name="pin"
            pattern="[0-9]{6}"
            required
            type="password"
          />
        </div>
        {mutation.error ? <p className="mt-4 text-sm font-semibold text-red-800" role="alert">{displayError(mutation.error)}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">Cancel</Button>
          <Button disabled={mutation.isPending} type="submit">{mutation.isPending ? "Saving…" : "Save PIN"}</Button>
        </div>
      </form>
    </div>
  );
}

function InvitationEditor({
  activeRole,
  item,
  locationId,
  onClose,
  onSent,
}: {
  activeRole: MembershipRole;
  item: StaffProfile;
  locationId: string;
  onClose: () => void;
  onSent: (invitation: Invitation) => void;
}) {
  const id = useId();
  const send = useMutation({
    mutationFn: (form: FormData) => {
      const role = String(form.get("role") ?? "staff") as MembershipRole;
      return inviteStaff({
        email: String(form.get("email") ?? "").trim(),
        location_ids: role === "admin" ? [] : [locationId],
        role,
        staff_profile_id: item.id,
      });
    },
    onSuccess: onSent,
  });
  const roles: MembershipRole[] =
    activeRole === "admin" ? ["staff", "manager", "admin"] : ["staff"];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-3 backdrop-blur-sm sm:p-6">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="mx-auto my-8 w-full max-w-lg rounded-3xl bg-surface p-5 shadow-2xl sm:p-7"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          send.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Account access
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              Invite {item.display_name}
            </h2>
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <p className="mt-3 text-sm leading-6 text-stone-500">
          The staff profile already exists. This invitation links their sign-in
          account without changing past records.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor={`${id}-email`}>Invitation email</Label>
            <Input
              className="mt-1.5"
              defaultValue={item.email ?? ""}
              id={`${id}-email`}
              name="email"
              required
              type="email"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-role`}>Role</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm capitalize"
              id={`${id}-role`}
              name="role"
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        </div>
        {send.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800" role="alert">
            {displayError(send.error)}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={send.isPending} type="submit">
            {send.isPending ? "Sending…" : "Send invitation"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function MembershipRoleControl({
  activeRole,
  locationId,
  person,
}: {
  activeRole: MembershipRole;
  locationId: string;
  person: StaffProfile;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (role: MembershipRole) => {
      const input = {
        location_id: role === "admin" ? null : locationId,
        role,
      };
      return person.membership
        ? changeMembership(person.membership.id, input)
        : grantMembership({ ...input, user_id: person.user_id! });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["staff", locationId] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });
  const roles: MembershipRole[] =
    activeRole === "admin"
      ? ["staff", "manager", "admin"]
      : ["staff", "manager"];
  const currentRole = person.membership?.role;
  const canChange =
    person.user_id !== null &&
    person.status === "active" &&
    !(activeRole === "manager" && currentRole === "admin");

  return (
    <div className="min-w-32">
      <Label className="sr-only" htmlFor={`membership-role-${person.id}`}>
        Role for {person.display_name}
      </Label>
      <select
        aria-label={`Role for ${person.display_name}`}
        className="h-9 w-full rounded-lg border border-stone-300 bg-surface px-2.5 text-xs font-semibold capitalize disabled:cursor-not-allowed disabled:opacity-60"
        disabled={!canChange || mutation.isPending}
        id={`membership-role-${person.id}`}
        onChange={(event) =>
          mutation.mutate(event.target.value as MembershipRole)
        }
        value={currentRole ?? ""}
      >
        {!currentRole ? <option value="">No access</option> : null}
        {currentRole === "admin" && !roles.includes("admin") ? (
          <option value="admin">Admin</option>
        ) : null}
        {roles.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </select>
      {mutation.error ? (
        <p
          className="mt-1 max-w-52 text-xs font-semibold text-red-800"
          role="alert"
        >
          {displayError(mutation.error)}
        </p>
      ) : null}
    </div>
  );
}

function PeopleWorkspacePage() {
  const shell = useShell();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const [editing, setEditing] = useState<StaffProfile | "new" | null>(null);
  const [inviting, setInviting] = useState<StaffProfile | null>(null);
  const [settingPin, setSettingPin] = useState<StaffProfile | null>(null);
  const [sent, setSent] = useState<Invitation | null>(null);
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });

  return (
    <>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            People
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Team
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Profiles preserve who performed every check; invitations add sign-in
            access separately.
          </p>
        </div>
        {canManage && locationId ? (
          <Button onClick={() => setEditing("new")}>
            <Plus className="size-4" /> Add team member
          </Button>
        ) : null}
      </header>

      {!canManage ? (
        <Card>
          <CardContent className="grid min-h-64 place-items-center p-8 text-center">
            <div className="max-w-md">
              <Users className="mx-auto size-7 text-stone-400" />
              <h2 className="mt-4 text-lg font-black">
                Team setup is manager-only
              </h2>
              <p className="mt-2 text-sm leading-6 text-stone-500">
                Your own training and tasks remain available in the kitchen
                workflow.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : !locationId ? (
        <EmptyState
          description="Choose or add a location before assigning its team."
          title="Choose a location"
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{shell.activeLocation?.name} team</CardTitle>
            <p className="text-sm text-stone-500">
              Active and historical staff profiles assigned to this location.
            </p>
          </CardHeader>
          <CardContent className="pt-3">
            {sent ? (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
                <UserRoundCheck className="mt-0.5 size-4 shrink-0" />
                <div>
                  <p className="font-bold">Invitation sent to {sent.email}</p>
                  <p className="mt-1 text-xs text-emerald-800">
                    It expires{" "}
                    {new Intl.DateTimeFormat("en-NZ", {
                      dateStyle: "medium",
                    }).format(new Date(sent.expires_at))}
                    .
                  </p>
                </div>
              </div>
            ) : null}
            {staff.isPending ? <LoadingState label="Loading the team" /> : null}
            {staff.error ? (
              <ErrorState
                message={displayError(staff.error)}
                onAction={() => void staff.refetch()}
                title="The team isn’t available"
              />
            ) : null}
            {staff.data?.length === 0 ? (
              <EmptyState
                calm
                description="Create a profile first, then invite that person if they need to sign in."
                title="No staff profiles yet"
              />
            ) : null}
            {staff.data && staff.data.length > 0 ? (
              <div className="divide-y divide-stone-200 border-t border-stone-200">
                {staff.data.map((person) => (
                  <div
                    className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center"
                    key={person.id}
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-stone-100 text-xs font-black text-stone-700">
                      {person.display_name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join("")
                        .toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-bold">
                          {person.display_name}
                        </p>
                        <Badge
                          tone={
                            person.status === "active" ? "positive" : "neutral"
                          }
                        >
                          {person.status}
                        </Badge>
                        {person.user_id ? <Badge>Account linked</Badge> : null}
                        {person.quick_pin_enabled ? <Badge tone="positive">PIN ready</Badge> : null}
                      </div>
                      <p className="mt-1 truncate text-xs text-stone-500">
                        {person.job_title ?? "Role not set"}
                        {person.email ? ` · ${person.email}` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-start gap-1">
                      {person.user_id ? (
                        <MembershipRoleControl
                          activeRole={shell.activeRole}
                          locationId={locationId}
                          person={person}
                        />
                      ) : null}
                      <Button
                        onClick={() => setSettingPin(person)}
                        size="sm"
                        variant="ghost"
                      >
                        <KeyRound className="size-3.5" /> {person.quick_pin_enabled ? "Change PIN" : "Set PIN"}
                      </Button>
                      <Button
                        onClick={() => setEditing(person)}
                        size="sm"
                        variant="ghost"
                      >
                        <Pencil className="size-3.5" /> Edit
                      </Button>
                      {shell.activeRole === "admin" ? (
                        <Link
                          className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-stone-600 hover:bg-stone-100 hover:text-stone-950"
                          search={{ staff_profile_id: person.id }}
                          to="/privacy"
                        >
                          <ShieldCheck className="size-3.5" /> Privacy
                        </Link>
                      ) : null}
                      {!person.user_id && person.status === "active" ? (
                        <Button
                          onClick={() => setInviting(person)}
                          size="sm"
                          variant="outline"
                        >
                          <MailPlus className="size-3.5" /> Invite
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {editing && locationId ? (
        <StaffEditor
          {...(editing === "new" ? {} : { item: editing })}
          locationId={locationId}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {inviting && locationId ? (
        <InvitationEditor
          activeRole={shell.activeRole}
          item={inviting}
          locationId={locationId}
          onClose={() => setInviting(null)}
          onSent={(invitation) => {
            setSent(invitation);
            setInviting(null);
          }}
        />
      ) : null}
      {settingPin && locationId ? (
        <QuickPinEditor
          item={settingPin}
          locationId={locationId}
          onClose={() => setSettingPin(null)}
        />
      ) : null}
    </>
  );
}

export function TeamWorkspacePage() {
  const [view, setView] = useState<"people" | "training">("people");

  return (
    <>
      <div className="mb-5 inline-flex rounded-xl border border-stone-200 bg-surface p-1 shadow-sm">
        <button
          className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-bold ${view === "people" ? "bg-emerald-800 text-white" : "text-stone-500 hover:bg-stone-50"}`}
          onClick={() => setView("people")}
          type="button"
        >
          <Users className="size-3.5" /> People
        </button>
        <button
          className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-bold ${view === "training" ? "bg-emerald-800 text-white" : "text-stone-500 hover:bg-stone-50"}`}
          onClick={() => setView("training")}
          type="button"
        >
          <GraduationCap className="size-3.5" /> Training
        </button>
      </div>
      {view === "people" ? <PeopleWorkspacePage /> : <TrainingWorkspacePage />}
    </>
  );
}
